import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  X,
  Sparkles,
  ChevronDown
} from 'lucide-react';

const pages = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Privacy', path: '/privacy' }
];

const authPages = [
  { name: 'Sign In', path: '/login' },
  { name: 'Sign Up', path: '/signup' }
];

export default function Navigation() {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Custom Link behavior to handle click and pass ref
  const LinkBehavior = React.forwardRef((props, ref) => {
    const { onClick, ...other } = props;
    const handleInternalClick = (event) => {
      if (onClick) {
        onClick(event);
      }
      handleDrawerToggle(); // Close the drawer
    };
    return <Link ref={ref} onClick={handleInternalClick} {...other} />;
  });

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Sparkles className="w-6 h-6" style={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
            Bean
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <X className="w-5 h-5" />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {pages.map((page) => (
          <ListItem
            key={page.name}
            component={LinkBehavior}
            to={page.path}
            sx={{
              color: isActive(page.path) ? theme.palette.primary.main : 'text.primary',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <ListItemText primary={page.name} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        {authPages.map((page) => (
          <ListItem
            key={page.name}
            component={LinkBehavior}
            to={page.path}
            sx={{
              color: isActive(page.path) ? theme.palette.primary.main : 'text.primary',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <ListItemText primary={page.name} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{ 
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              color: 'inherit',
              mr: 2
            }}
          >
            <Sparkles className="w-6 h-6" style={{ color: theme.palette.primary.main }} />
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Bean
            </Typography>
          </Box>

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Desktop Menu */}
          {!isMobile && (
            <>
              <Box sx={{ flexGrow: 1, display: 'flex', gap: 2 }}>
                {pages.map((page) => (
                  <Button
                    key={page.name}
                    component={Link}
                    to={page.path}
                    sx={{
                      color: isActive(page.path) ? theme.palette.primary.main : 'text.primary',
                      '&:hover': {
                        bgcolor: 'transparent',
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    {page.name}
                  </Button>
                ))}
              </Box>

              {/* Auth Buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {authPages.map((page) => (
                  <Button
                    key={page.name}
                    component={Link}
                    to={page.path}
                    variant={page.name === 'Sign Up' ? 'contained' : 'outlined'}
                    sx={{
                      ...(page.name === 'Sign Up' && {
                        bgcolor: theme.palette.primary.main,
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark
                        }
                      })
                    }}
                  >
                    {page.name}
                  </Button>
                ))}
              </Box>
            </>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box',
            width: 250
          }
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
}
